import { FC } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Feature {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

interface FeatureItemProps extends Feature {
  reverse?: boolean;
}

const FeatureItem: FC<FeatureItemProps> = ({ title, description, imageUrl, imageAlt, reverse = false }) => {
  return (
    <div className={`flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 md:gap-16 py-12 px-6 md:px-12`}>
      {/* Imagen */}
      <div className="w-full md:w-1/2">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-auto rounded-2xl shadow-2xl"
          loading="lazy"
        />
      </div>
      
      {/* Contenido */}
      <div className="w-full md:w-1/2 text-white">
        <h3 className="text-2xl md:text-3xl font-bold mb-6">{title}</h3>
        <p className="text-lg leading-relaxed opacity-90">{description}</p>
      </div>
    </div>
  );
};

export const FeatureGrid: FC = () => {
  const navigate = useNavigate();

  const handleStartTrial = () => {
    navigate('/register');
  };

  const features: Feature[] = [
    {
      title: "Preparando a la Próxima Generación para un Futuro Innovador y Seguro",
      description: "En un mundo donde el 65% de los niños escolarizados hoy terminarán trabajando en empleos que aún no existen, y teniendo en cuenta que el 43% de los adultos con bajos niveles de competencia financiera son más propensos a enfrentar dificultades económicas, la educación financiera y de negocios se convierte en una herramienta esencial para preparar a los niños para un futuro económico incierto y altamente tecnológico. Estudios muestran que los jóvenes que reciben educación financiera desde temprana edad no solo están mejor preparados para gestionar sus propias finanzas con un 34% menos de deuda personal en su vida adulta, sino que también muestran una mayor propensión al emprendimiento, vital en una era dominada por la innovación y el cambio rápido!",
      imageUrl: "https://media.financieramente.kids/img1.jpg",
      imageAlt: "Niño estudiando finanzas y tecnología"
    },
    {
      title: "Despierta su Espíritu Emprendedor Hoy",
      description: "¿Imagina a su hijo lanzando su primer negocio antes de terminar la escuela secundaria? En FinancieraMente, fomentamos el espíritu emprendedor desde una edad temprana. Nuestros módulos interactivos enseñan desde la creación de ideas hasta la ejecución de un plan de negocios, todo mientras se divierten. Al inscribir a su hijo hoy, le está abriendo las puertas a un mundo de oportunidades empresariales y a una vida de autosuficiencia y éxito.",
      imageUrl: "https://media.financieramente.kids/img2.jpg",
      imageAlt: "Joven emprendedor desarrollando ideas de negocio"
    },
    {
      title: "Construye una Base Sólida de Conocimientos Financieros",
      description: "En un mundo donde la educación financiera a menudo se pasa por alto en los currículos escolares, FinancieraMente ofrece a su hijo la oportunidad de obtener una ventaja. A través de juegos educativos y lecciones interactivas, enseñamos conceptos financieros fundamentales que son esenciales para la gestión inteligente del dinero. Ayude a su hijo a aprender a ahorrar, invertir y gestionar recursos desde joven, estableciendo el camino hacia la seguridad financiera y la libertad.",
      imageUrl: "https://media.financieramente.kids/img3.jpg",
      imageAlt: "Niños aprendiendo conceptos financieros de forma interactiva"
    },
    {
      title: "Cultiva Principios y Valores de una Mentalidad Positiva",
      description: "Más allá de las finanzas y los negocios, en FinancieraMente nos dedicamos a moldear niños y jóvenes de gran carácter. Nuestros cursos incluyen lecciones sobre ética, liderazgo y resiliencia, cruciales para el éxito en todos los aspectos de la vida. Al unirse a nuestra comunidad hoy, su hijo no solo aprenderá a hacer dinero, sino también a hacer una diferencia positiva en el mundo.",
      imageUrl: "https://media.financieramente.kids/img4.jpg",
      imageAlt: "Familia compartiendo valores y principios positivos"
    },
    {
      title: "Preparación para el Futuro en una Era Digital y Moderna",
      description: "Vivimos en una era tecnológica que evoluciona rápidamente y donde las habilidades digitales son tan importantes como la alfabetización tradicional. FinancieraMente integra tecnología de punta en cada lección, preparando a su hijo no solo para enfrentar el futuro, sino para moldearlo. Suscríbase hoy y dé a su hijo la ventaja de comprender y utilizar la tecnología financiera que definirá las próximas décadas.",
      imageUrl: "https://media.financieramente.kids/img5.jpg",
      imageAlt: "Niños interactuando con tecnología financiera moderna"
    }
  ];

  return (
    <section className="w-full bg-gradient-to-br from-primary via-secondary to-accent">
      <div className="w-full py-12">
        <div className="max-w-[90rem] mx-auto">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              {...feature}
              reverse={index % 2 !== 0}
            />
          ))}
          
          {/* Botón de acción */}
          <div className="flex justify-center py-12">
            <Button
              onClick={handleStartTrial}
              size="lg"
              className="bg-white hover:bg-white/90 text-primary text-lg px-8 py-6 h-auto font-semibold shadow-xl transform transition-all duration-200 hover:scale-105 hover:shadow-2xl"
            >
              Comienza Tu Prueba Gratuita
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
